/**
 * Helpers for the masked MM/DD/YYYY {@link DateInput}. Kept standalone from
 * DateOfBirthInput on purpose — DOB carries bespoke age/future validation and a
 * display-string contract, whereas these are calendar-validity-only and convert
 * to/from ISO `YYYY-MM-DD` (the contract every date call site already stores).
 */

const ISO_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const DISPLAY_RE = /^(\d{2})\/(\d{2})\/(\d{4})$/;

/**
 * Masks raw input into MM/DD/YYYY: strips non-digits, caps at 8 digits, and
 * auto-inserts slashes after the month and day. Mirrors DOB's `formatDobValue`.
 */
export function formatDateValue(raw: string): string {
  const digits = raw.replace(/\D/g, "").slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
}

/**
 * Validates a complete MM/DD/YYYY display string for calendar validity.
 * Returns an error message, or null when the date is a valid complete date.
 *
 * By default there is no past/future restriction (some dates are deliberately
 * years out). Pass `{ notInPast: true }` for fields where a past date is
 * meaningless — e.g. an expiration you're adding today — to also reject any
 * date before the current local day (today itself is allowed).
 */
export function validateCalendarDate(
  display: string,
  options?: { notInPast?: boolean }
): string | null {
  if (!display || display.length < 10) return "Please use MM/DD/YYYY format.";
  const match = display.match(DISPLAY_RE);
  if (!match) return "Please use MM/DD/YYYY format.";

  const [, mm, dd, yyyy] = match;
  const month = parseInt(mm, 10);
  const day = parseInt(dd, 10);
  const year = parseInt(yyyy, 10);

  if (month < 1 || month > 12) return "Invalid date.";
  if (day < 1 || day > 31) return "Invalid date.";
  if (year < 1900 || year > 2100) return "Invalid date.";

  const date = new Date(year, month - 1, day);
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return "Invalid date.";
  }

  if (options?.notInPast) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (date < today) return "Date can't be in the past.";
  }
  return null;
}

/** Hotel creates require today-or-later check-in; edits must preserve historical stays. */
export function validateHotelCheckInDate(
  display: string,
  isEditMode: boolean
): string | null {
  return validateCalendarDate(display, { notInPast: !isEditMode });
}

/** Converts a MM/DD/YYYY display string to ISO `YYYY-MM-DD`, or null if invalid/incomplete. */
export function displayToIso(display: string): string | null {
  if (validateCalendarDate(display) !== null) return null;
  const [mm, dd, yyyy] = display.split("/");
  return `${yyyy}-${mm}-${dd}`;
}

/** Converts an ISO `YYYY-MM-DD` string to MM/DD/YYYY for display, or "" if empty/invalid. */
export function isoToDisplay(iso: string): string {
  if (!iso) return "";
  const match = iso.match(ISO_RE);
  if (!match) return "";
  const [, yyyy, mm, dd] = match;
  return `${mm}/${dd}/${yyyy}`;
}
